import { useCallback, useState, useEffect } from 'react';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';

import { useOneTimeOrder } from '../../OneTimeOrderProvider';
import { ONE_TIME_ORDER_STEPS } from '../../../../utils/oneTimeOrder';
import { getTrackerIdEventName, SUBSCRIPTION_TRACKER_TYPES, TRACKER_IDS } from '../../../../config/tracker';
import NavigationBar from '../../../../global/NavigationBar';
import useScrollToTop from '../../../../utils/hooks/useScrollToTop';
import getStripePromise from '../../../../utils/stripe';
import { createOrderCheckoutSession } from '../../../../utils/service';
import MEMBERSHIP_OFFERED_ITEMS, { getFilteredMembershipItems } from '../../../../utils/membership';

import './OneTimeOrderPayment.scss';
import { QUANTITY_TYPES } from '../../../../config/config';
import { getGenericError } from '../../../../utils/errors';
import { SUBSCRIPTION_PAYMENT_TYPES } from '../../../../utils/subscriptions';

const stripePromise = getStripePromise();

const {
  oneTimeOrder: {
    payment: {
      [SUBSCRIPTION_TRACKER_TYPES.VIEW]: trackerView,
      [SUBSCRIPTION_TRACKER_TYPES.CLOSE]: trackerClose,
    },
  },
} = TRACKER_IDS;

const OneTimeOrderPayment = () => {
  useScrollToTop();
  const {
    orderItems,
    address,
    setStep,
    shippingPrice,
    posthog,
    searchParams,
  } = useOneTimeOrder();

  const [clientSecret, setClientSecret] = useState(null);
  const [errorStripe, setErrorStripe] = useState(null);

  const handleBack = useCallback(() => {
    posthog.capture(getTrackerIdEventName(trackerClose));
    setStep(ONE_TIME_ORDER_STEPS.SELECTION);
  }, [posthog]);

  const fetchClientSecret = useCallback(async (products, shippingAddress, orderShippingPrice) => {
    const membershipItems = getFilteredMembershipItems(SUBSCRIPTION_PAYMENT_TYPES.ONE_TIME)
      .map(item => item.id);

    const {
      data,
      error: createCheckoutSessionError,
    } = await createOrderCheckoutSession({
      products: Object.entries(products)
        .filter(
          ([productType, quantity]) => membershipItems.includes(productType) && quantity > 0,
        )
        .map(([productType, quantity]) => {
          const { id, quantityLabel } = MEMBERSHIP_OFFERED_ITEMS[productType] ?? {};

          return {
            productType: id,
            quantity,
            description: quantity > 1
              ? quantityLabel[QUANTITY_TYPES.PLURAL]
              : quantityLabel[QUANTITY_TYPES.SINGULAR],
          };
        }),
      shippingAddress,
      shippingPrice: orderShippingPrice,
      searchParams,
    });

    if (createCheckoutSessionError || !data?.clientSecret) {
      setErrorStripe(getGenericError());
      setClientSecret(null);
      return;
    }

    setClientSecret(data.clientSecret);
    setErrorStripe(null);
  }, []);

  useEffect(() => {
    if (!clientSecret && orderItems && address) {
      fetchClientSecret(orderItems, address, shippingPrice);
    }
  }, [clientSecret, orderItems, address, shippingPrice]);

  useEffect(() => {
    posthog.capture(getTrackerIdEventName(trackerView));
  }, []);

  return (
    <div className="OneTimeOrderPayment">
      <NavigationBar
        className="OneTimeOrderPayment__navigationBar"
        onGoBack={handleBack}
      />
      {errorStripe && (
        <div className="error">{errorStripe}</div>
      )}
      {clientSecret && (
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={{ clientSecret }}
        >
          <EmbeddedCheckout className="OneTimeOrderPayment__embeddedCheckout" />
        </EmbeddedCheckoutProvider>
      )}
    </div>
  );
};

export default OneTimeOrderPayment;
